import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { Workflow } from "@acto/taskpane/core/domain";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";

const WorkflowsQuery = graphql(`
  query GetWorkflows($first: Int!, $after: String, $filter: WorkflowFilter) {
    workflows(first: $first, after: $after, filter: $filter) {
      edges {
        cursor
        node {
          id
          name
          isActive
          isCustomQuery
          repeat
          activeTodosCount
          query {
            groupConjunction
            groups {
              conjunction
              conditions {
                filter
                params {
                  name
                  operator
                  stringValue
                  stringValues
                  numericValue
                  intValue
                  dateValue
                  boolValue
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

interface UseWorkflowsOptions {
  searchTerm?: string;
  statusFilter?: "all" | "active" | "inactive";
}

const DEFAULT_PAGE_SIZE = 10;

export function useWorkflows({ searchTerm, statusFilter = "all" }: UseWorkflowsOptions) {
  const filter = useMemo(() => {
    const filter: { isActive?: boolean; searchTerm?: string } = {};

    if (statusFilter === "active") filter.isActive = true;
    else if (statusFilter === "inactive") filter.isActive = false;

    if (searchTerm) filter.searchTerm = searchTerm;

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [statusFilter, searchTerm]);

  const { data, isPending, isFetching, error, hasNextPage, fetchNextPage, refetch } = usePagination({
    query: WorkflowsQuery,
    variables: { filter },
    limit: DEFAULT_PAGE_SIZE,
    connection: (data) => data.workflows,
  });

  const workflows = useMemo(() => data?.map(Workflow.fromGraphql) ?? [], [data]);

  return {
    workflows,
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  };
}
